import { stopAction } from "../../../Actions/Index";
import { createOrUpdateBookingCommentAction } from "../../../Actions/LogicActions/BookingDetail/CreateOrUpdateBookingCommentAction";
import { getJourneyStops } from "../../../Actions/LogicActions/BookingDetail/GetBookingJourneyDetailAction";
import { journeyTabChangeAction } from "../../../Actions/LogicActions/BookingDetail/JourneyTabChangeAction";
import { getAllFmlmJourneyAction } from "../../../Actions/UIActions/FmlmViewInformation/GetAllFmlmJourneyAction";
import { opencloseLocationMap } from "../../../Actions/UIActions/Search/OpenCloseLocationMap";
import { updatedStopsAction } from "../../../Actions/UIActions/Search/UpdatedStopsAction";
import { DEFAULT_TIMEZONE } from "../../../config/global";
import { _eventBus } from "../../../core/events/EventBus";
import { StepDefinition } from "../../../core/flows/StepDefinition";
import { StepFlow } from "../../../core/flows/StepFlow";
import stopManager from "../../../Lib/Utils/StopManager";
import { getLocaleFromCookie } from "../../../Lib/Utils/getLocaleFromCookie";
import { getSearchSelectedPassenger } from "../../../Lib/Utils/passenger";
import { journeyPlannerTree } from "../../../Orchestrator/ConstantTree/JourneyPlanner/JourneyPlannerTree";
import { getRecoilStateValue } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ISelectedTrainLegs, SearchStateType, StationsListType } from "../../../Orchestrator/Types/Index";
import { IJourneyPlannerConfirmEventPayload } from "../../../Orchestrator/Types/Interface/ActionParams/CreateOrUpdateBookingCommentActionParams";
import { IGetJourneyPlannerSearchEventPayload } from "../../../Orchestrator/Types/Interface/ActionParams/GetAllFmlmJourneyActionParams";
import { IJourneyTabChangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/journeyTabChangeActionParams";
import { LocationPermission } from "../../../Orchestrator/Types/Types/FmlmSearch/FmlmSearchType";
import { SearchContext } from "../../Common/Search/Context/SearchContext";
import dayjs from "dayjs";

class JourneyPlannerInitStep extends StepFlow {
  get stepName() { return "JourneyPlannerInit_Step"; }

  async onEventSetup(): Promise<void> {
    this.registerEventSubscription(
      _eventBus.on<{ selectedTrainLegs: ISelectedTrainLegs }, void>({
        eventName: journeyPlannerTree.popup.init,
        handler: async (payload) => {
          const { selectedTrainLegs } = payload.data!;

          if (selectedTrainLegs) {
            // Check if current trip is inbound only
            if (Object.keys(selectedTrainLegs).length === 1 && Object.keys(selectedTrainLegs)[0] === "inbound") {
              journeyTabChangeAction.setParams({ value: TripTypeEnum.IN_BOUND });
              journeyTabChangeAction.run();
            } else {
              journeyTabChangeAction.setParams({ value: TripTypeEnum.OUT_BOUND });
              journeyTabChangeAction.run();
            }
          }
        }
      })
    );

    this.registerEventSubscription(
      _eventBus.on<{ visible: boolean, locationPermission: LocationPermission }, void>({
        eventName: journeyPlannerTree.location.click,
        handler: async (payload) => {
          let locationMapVisible = payload.data?.visible || false;
          let locationPermission = payload.data?.locationPermission!;

          // fetch and set location data to recoil
          await stopAction.execute();
          const data = await getRecoilStateValue("stopsResponseAtom")
          await updatedStopsAction.setParams({ data });
          await updatedStopsAction.execute();

          opencloseLocationMap.setParams({ isVisible: locationMapVisible, locationPermission: locationPermission });
          await opencloseLocationMap.execute();
          this.next();
        }
      })
    )

    this.registerEventSubscription(
      _eventBus.on<IGetJourneyPlannerSearchEventPayload, void>({
        eventName: journeyPlannerTree.journey.search,
        handler: async (payload) => {
          const { journeyPlannerTrainTypeTab, tripDetails } = payload.data!;

          let currentTrainDetail = (journeyPlannerTrainTypeTab === TripTypeEnum.OUT_BOUND ? tripDetails.outbound : tripDetails.inbound)?.[0] || null;
          if (!currentTrainDetail) {
            console.error(`No ${journeyPlannerTrainTypeTab} train legs found.`);
            return;
          }

          const bookingDate = dayjs(currentTrainDetail?.startTime).tz(DEFAULT_TIMEZONE);
          const journeyStops = getJourneyStops(journeyPlannerTrainTypeTab)
          const passengerDetail = getSearchSelectedPassenger(currentTrainDetail.passengers);
          
          const currentTripDetails = journeyPlannerTrainTypeTab === TripTypeEnum.OUT_BOUND ? tripDetails.outbound : tripDetails.inbound;
          if (!currentTripDetails || currentTripDetails.length === 0) {
            return;
          }

          const mainOrigin = currentTripDetails[0]?.origin;
          const mainDestination = currentTripDetails[currentTripDetails.length - 1]?.destination;

          if (!mainOrigin || !mainDestination) {
            return;
          }

          const updatedStopsData = await getRecoilStateValue<StationsListType>("updatedStopsAtom") || [];
          const stopsData: any = updatedStopsData.length > 0 ? updatedStopsData : (await getRecoilStateValue<StationsListType>("stopsResponseAtom") || []);

          const getStopCoordinates = async (stopPlaceRef: string): Promise<{ lat: number; lon: number; name: string; stopCode?: string }> => {
            if (!stopPlaceRef) {
              return { lat: 0, lon: 0, name: "", stopCode: "" };
            }

            for (const stop of stopsData) {
              if (stop.id === stopPlaceRef) {
                return {
                  lat: stop.geoPosition?.lat || stop.geoPosition?.latitude || 0,
                  lon: stop.geoPosition?.lng || stop.geoPosition?.longitude || 0,
                  name: stop.name || stopManager.getStopNameById(stopPlaceRef) || "",
                  stopCode: stop.id,
                };
              }

              for (const subStation of stop.subStations || []) {
                if (subStation.id === stopPlaceRef) {
                  return {
                    lat: subStation.geoPosition?.lat || subStation.geoPosition?.latitude || 0,
                    lon: subStation.geoPosition?.lng || subStation.geoPosition?.longitude || 0,
                    name:
                      subStation.name ||
                      stopManager.getStopNameById(stopPlaceRef) ||
                      "",
                    stopCode: subStation.id,
                  };
                }
              }
            }

            return {
              lat: 0,
              lon: 0,
              name: stopManager.getStopNameById(stopPlaceRef) || stopPlaceRef,
              stopCode: stopPlaceRef,
            };
          };

          let originData;
          if (journeyStops?.origin?.display_name) {
            originData = {
              stopCode: journeyStops.origin.stopCode || "",
              display_name: journeyStops.origin.display_name,
              lat: journeyStops.origin.lat || 0,
              lon: journeyStops.origin.lon || 0
            };
          } else {
            const mainOriginData = await getStopCoordinates(mainOrigin.stopPlaceRef);
            originData = {
              stopCode: mainOriginData.stopCode || mainOrigin.stopPlaceRef,
              display_name: mainOriginData.name || mainOrigin.stopPlaceRef,
              lat: mainOriginData.lat,
              lon: mainOriginData.lon
            };
          }

          let destinationData;
          if (journeyStops?.destination?.display_name) {
            destinationData = {
              stopCode: journeyStops.destination.stopCode || "",
              display_name: journeyStops.destination.display_name,
              lat: journeyStops.destination.lat || 0,
              lon: journeyStops.destination.lon || 0
            };
          } else {
            const mainDestinationData = await getStopCoordinates(mainDestination.stopPlaceRef);
            destinationData = {
              stopCode: mainDestinationData.stopCode || mainDestination.stopPlaceRef,
              display_name: mainDestinationData.name || mainDestination.stopPlaceRef,
              lat: mainDestinationData.lat,
              lon: mainDestinationData.lon
            };
          }

          const searchState: SearchStateType = {
            from: originData?.stopCode || "",
            fromName: originData?.display_name,
            fromGeoPosition: {
              latitude: originData?.lat || 0,
              longitude: originData?.lon || 0
            },
            to: destinationData?.stopCode || "",
            toName: destinationData?.display_name,
            toGeoPosition: {
              latitude: destinationData?.lat || 0,
              longitude: destinationData?.lon || 0
            },
            calc: "oneway",
            passengers: passengerDetail.passengerCounts,
            outboundDate: bookingDate,
            inboundDate: null,
            date: bookingDate.format("YYYY-MM-DD"),
            fromfmlmSearch: true,
            tofmlmSearch: true
          }

          const lang = await getLocaleFromCookie();

          getAllFmlmJourneyAction.setParams({
            payload: {
              departureTime: dayjs(searchState.outboundDate).valueOf(),
              fromLat: searchState.fromGeoPosition?.latitude || 0,
              fromLon: searchState?.fromGeoPosition?.longitude || 0,
              fromName: searchState?.fromName || "",
              language: lang,
              toLat: searchState?.toGeoPosition?.latitude || 0,
              toLon: searchState?.toGeoPosition?.longitude || 0,
              toName: searchState?.toName || "",
            },
          })
          await getAllFmlmJourneyAction.execute();
        }
      })
    )

    this.registerEventSubscription(
      _eventBus.on<IJourneyPlannerConfirmEventPayload, void>({
        eventName: journeyPlannerTree.journey.confirm,
        handler: async (payload) => {
          const { bookingResponse, journeyPlannerState, bookedJourneyComment } = payload.data!;

          if (!bookingResponse.booking.id) {
            console.error("No booking ID found in booking response.");
          }

          createOrUpdateBookingCommentAction.setParams({
            bookingId: bookingResponse.booking.id!,
            commentId: bookedJourneyComment?.id || undefined,
            comment: journeyPlannerState
          })
          await createOrUpdateBookingCommentAction.execute();
        }
      })
    )

    this.registerEventSubscription(
      _eventBus.on<IJourneyTabChangeActionParams, void>({
        eventName: journeyPlannerTree.journey.tab.change,
        handler: async (payload) => {
          journeyTabChangeAction.setParams(payload.data!);
          journeyTabChangeAction.execute();
        }
      })
    )
  }

  async onExecute(): Promise<void> {
    console.log("Journey Planner Initial Step Executed.....");

    const searchContext = this.getCtx<SearchContext>("searchContext");
    if (!searchContext) {
      const newSearchContext = new SearchContext()
      this.setCtx<SearchContext>("searchContext", newSearchContext)
    }
  }
}

export const JourneyPlannerInitStepDeclaration = new StepDefinition({
  stepName: "JourneyPlannerInit_Step",
  stepClass: JourneyPlannerInitStep
});
